import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { SLASH_COMMANDS } from '@/lib/commands/SlashRegistry';
import { useUIStore } from '@/lib/store/uiStore';

export function SlashPalette({ onPick }: { onPick?: (name: string) => void }) {
  const open = useUIStore((s) => s.slashOpen);
  const setOpen = useUIStore((s) => s.setSlashOpen);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/10 animate-fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-[22%] z-50 w-[520px] -translate-x-1/2 rounded-lg border border-border bg-canvas shadow-menu animate-fade-in"
          aria-label="Command palette"
        >
          <Dialog.Title className="sr-only">Slash commands</Dialog.Title>
          <Dialog.Description className="sr-only">
            Type a slash command name to insert or run it.
          </Dialog.Description>

          <Command className="flex flex-col">
            <div className="border-b border-border px-3 py-2.5">
              <Command.Input
                placeholder="Type a command…"
                className="w-full bg-transparent text-[14px] text-fg outline-none placeholder:text-fg-subtle"
                autoFocus
              />
            </div>

            <Command.List className="max-h-[360px] overflow-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-[13px] text-fg-subtle">
                No commands.
              </Command.Empty>

              {(['workflow', 'agent', 'session', 'utility'] as const).map((cat) => {
                const items = SLASH_COMMANDS.filter((c) => c.category === cat);
                if (!items.length) return null;
                return (
                  <Command.Group
                    key={cat}
                    heading={cat[0].toUpperCase() + cat.slice(1)}
                    className="px-1 pt-1 text-2xs uppercase tracking-wide text-fg-subtle [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
                  >
                    {items.map((c) => (
                      <Command.Item
                        key={c.name}
                        value={`${c.name} ${c.description}`}
                        onSelect={() => {
                          onPick?.(c.name);
                          setOpen(false);
                        }}
                        className="menu-item data-[selected=true]:bg-surface-2"
                      >
                        <span className="text-fg-muted">/</span>
                        <span className="font-medium text-fg">{c.name}</span>
                        {c.args && (
                          <span className="text-2xs text-fg-subtle">{c.args}</span>
                        )}
                        <span className="truncate text-[12px] text-fg-muted">
                          — {c.description}
                        </span>
                        {c.hint && <kbd className="ml-auto">{c.hint}</kbd>}
                      </Command.Item>
                    ))}
                  </Command.Group>
                );
              })}
            </Command.List>

            <div className="flex items-center gap-3 border-t border-border px-3 py-1.5 text-2xs text-fg-subtle">
              <span>
                <kbd>↑</kbd> <kbd>↓</kbd> navigate
              </span>
              <span>
                <kbd>↵</kbd> select
              </span>
              <span>
                <kbd>esc</kbd> close
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
